import { useMemo, useRef } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { CardActionUploadGroup } from '@/common/components/CardActionUploadGroup/CardActionUploadGroup';
import { ControlledCheckbox } from '@/common/components/Fields/ControlledCheckbox/ControlledCheckbox';
import { ControlledDateField } from '@/common/components/Fields/ControlledDateField/ControlledDateField';
import { ControlledRadioButtonGroup } from '@/common/components/Fields/ControlledRadioButtonGroup/ControlledRadioButtonGroup';
import { ControlledSelectField } from '@/common/components/Fields/ControlledSelectField/ControlledSelectField';
import { ControlledTextField } from '@/common/components/Fields/ControlledTextField/ControlledTextField';
import { ControllerContext } from '@/common/components/Fields/ControllerContext/ControllerContext';
import { Form } from '@/common/components/Form/Form';
import { Icon } from '@/common/components/Icons/Icon';
import { Modal } from '@/common/components/Modal/Modal';
import { TOverlayRef } from '@/common/components/Overlay/Overlay.types';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { determineKinships } from '@/common/constants/kinship';
import { MARKET } from '@/common/constants/markets';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { useMarket } from '@/common/hooks/useMarket';
import {
  useAddFamilyMember,
  useGetFilteredFamilyMembers,
} from '@/common/integrations/menhir/employee/employee/employee.hooks';
import {
  IFamilyMember,
  MenhirFamilyMemberType,
} from '@/common/integrations/menhir/employee/employee/employee.types';
import { formatDateISO } from '@/common/utils/date';
import { isErrorObject } from '@/common/utils/error';
import { determineTaxIdTypeByMarket } from '@/common/utils/tax';
import { notify } from '@/common/utils/toast/toast';
import { genders } from '@coverflex-tech/hypernova-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { endOfToday } from 'date-fns';
import {
  TAddFamilyMemberFormData,
  familyMemberFormSchema,
} from './FamilyMemberForm.schema';

export type TAddFamilyMemberProps = {
  initialValues?: {
    type?: MenhirFamilyMemberType;
    dependant?: string;
  };
  successMessage?: string;
  onClose: () => void;
  onMemberAdded?: (member: IFamilyMember) => void;
};

export function FamilyMemberFormModal({
  onClose,
  initialValues,
  successMessage,
  onMemberAdded,
}: TAddFamilyMemberProps) {
  const { t } = useTranslation();
  const { spouse } = useGetFilteredFamilyMembers();
  const { mutateAsync: addFamilyMember } = useAddFamilyMember();
  const { marketMatch } = useMarket();
  const { company } = useEmployeeDetails();
  const initialKinship = initialValues?.type;
  const initialDependant =
    initialValues?.dependant === 'true' ? 'dependant' : '';
  const modalRef = useRef<TOverlayRef>(null);

  const market = company.market.slug;

  const kinshipDegreeProofRequired =
    company.settings?.kinshipDegreeProofRequired;

  const kinshipList = useMemo(() => determineKinships(market), [market]);
  const kinshipToString = kinshipList.map((kinship) => ({
    ...kinship,
    label: t(kinship.label),
  }));

  const genderToString = genders.map((gender) => ({
    ...gender,
    label: t(gender.label),
  }));

  const onSubmit = async ({
    fullName,
    gender,
    taxId,
    birthdayDate,
    shortName,
    kinship,
    dependant,
    nonSufficient,
    files,
  }: TAddFamilyMemberFormData) => {
    try {
      const addedMember = await addFamilyMember({
        fullName,
        taxId: taxId
          ? {
              type: determineTaxIdTypeByMarket(market),
              value: taxId,
            }
          : undefined,
        birthdate: formatDateISO(birthdayDate),
        type: kinship.value,
        shortName,
        files,
        ...(() => {
          switch (market) {
            case MARKET.PT:
            case MARKET.ES:
              return { gender: gender?.value };
            case MARKET.IT:
              return {
                dependant: dependant === 'dependant',
                nonSufficient: nonSufficient === 'non_sufficient',
              };
            default:
              return undefined;
          }
        })(),
      });

      notify({
        type: 'success',
        message:
          successMessage ??
          t('dashboard.add_family_member.notification.description', {
            name: addedMember?.shortName,
          }),
      });
      modalRef.current?.close();
      onMemberAdded?.(addedMember);
    } catch (error) {
      if (isErrorObject(error)) {
        const errorMapper: Record<string, 'taxId' | 'fullName'> = {
          tax_id: 'taxId',
          name: 'fullName',
        };
        Object.keys(error.errors).forEach((errorKey: string) => {
          const mappedErrorKey = errorMapper[errorKey];
          if (mappedErrorKey) {
            return methods.setError(mappedErrorKey, {
              type: 'custom',
              message: t(`errors.api.${errorKey}`),
            });
          }
        });
      }

      notify({ message: t('errors.api.generic'), type: 'error' });
    }
  };

  const methods = useForm<TAddFamilyMemberFormData>({
    mode: 'onTouched',
    defaultValues: {
      fullName: '',
      taxId: '',
      birthdayDate: undefined,
      shortName: '',
      kinship: kinshipToString.find((k) => k.value === initialKinship),
      gender: undefined,
      dependant: initialDependant,
      nonSufficient: '',
      certification: false,
      files: [],
    },
    resolver: zodResolver(
      familyMemberFormSchema({
        market,
        hasSpouse: !!spouse,
        kinshipVerificationRequired:
          !!company.settings?.kinshipDegreeProofRequired,
      })
    ),
  });

  return (
    <Modal
      title={t('dashboard.add_family_member.title')}
      icon={<Icon icon="user-plus" />}
      iconBadgeColor="accentSecondary20"
      onClose={onClose}
      modalRef={modalRef}
      footer={({ close }) => (
        <Stack horizontal gap="md">
          <Button onPress={close} variant="secondary">
            {t('general.navigation.cancel')}
          </Button>
          <Button
            onPress={methods.handleSubmit(onSubmit)}
            className="flex-1"
            loading={methods.formState.isSubmitting}
          >
            {t('general.navigation.save')}
          </Button>
        </Stack>
      )}
    >
      <Stack gap="lg">
        <BodyText size="sm" color="neutral20">
          {t('dashboard.add_family_member.description')}
        </BodyText>
        <FormProvider {...methods}>
          <Form onSubmit={methods.handleSubmit(onSubmit)}>
            <Stack gap="md">
              <ControlledSelectField
                name="kinship"
                label={t('dashboard.add_family_member.fields.kinship')}
                options={kinshipToString}
                disabled={!!initialKinship}
              />
              <ControlledTextField
                name="fullName"
                label={t('dashboard.add_family_member.fields.full_name')}
              />
              <ControlledTextField
                name="shortName"
                label={`${t(
                  'dashboard.add_family_member.fields.short_name'
                )} (${t('dashboard.add_family_member.fields.optional')})`}
                maxLength={11}
              />
              <ControllerContext<TAddFamilyMemberFormData>
                render={({ watch }) => {
                  const kinship = watch('kinship');
                  return (
                    <ControlledTextField
                      name="taxId"
                      label={`${t('dashboard.add_family_member.fields.tax_id')}${
                        market === MARKET.ES && kinship?.value === 'child'
                          ? ` ${t('form.fields.optional')}`
                          : ''
                      }`}
                    />
                  );
                }}
              />
              <ControlledDateField
                name="birthdayDate"
                label={t('dashboard.add_family_member.fields.birthday_date')}
                maxDate={endOfToday()}
              />
              {marketMatch(MARKET.PT, MARKET.ES) && (
                <ControlledSelectField
                  name="gender"
                  options={genderToString}
                  label={`${t('dashboard.add_family_member.fields.gender')}${
                    marketMatch(MARKET.ES)
                      ? ` ${t('form.fields.optional')}`
                      : ''
                  }`}
                  hint={t('general.gender.disclaimer')}
                />
              )}

              {market === MARKET.IT && (
                <>
                  <ControlledRadioButtonGroup
                    title={t('dashboard.add_family_member.fields.dependant')}
                    options={[
                      { label: t('general.values.yes'), value: 'dependant' },
                      { label: t('general.values.no'), value: 'not_dependant' },
                    ]}
                    name="dependant"
                  />
                  <ControlledRadioButtonGroup
                    title={t('dashboard.add_family_member.fields.sufficient')}
                    options={[
                      {
                        label: t('general.values.yes'),
                        value: 'non_sufficient',
                      },
                      {
                        label: t('general.values.no'),
                        value: 'not_non_sufficient',
                      },
                    ]}
                    name="nonSufficient"
                  />
                  <ControlledCheckbox
                    name="certification"
                    label={
                      <BodyText bold>
                        {t(
                          'dashboard.add_family_member.fields.label_certification'
                        )}
                        &nbsp;
                        <BodyText bold as="span">
                          {t(
                            'dashboard.add_family_member.fields.certification.title'
                          )}
                        </BodyText>
                      </BodyText>
                    }
                    description={t(
                      'dashboard.add_family_member.fields.certification.description'
                    )}
                  />
                </>
              )}
              {kinshipDegreeProofRequired ? (
                <Controller<TAddFamilyMemberFormData, 'files'>
                  name="files"
                  render={({ field, fieldState }) => {
                    const kinship = methods.watch('kinship');
                    return (
                      <CardActionUploadGroup
                        size="sm"
                        title={t(
                          'dashboard.add_family_member.fields.kinship_degree_proof.title'
                        )}
                        description={
                          kinship ? (
                            <Translate
                              i18nKey={`dashboard.add_family_member.fields.kinship_degree_proof.description.${market}.${kinship.value}`}
                            />
                          ) : (
                            <Translate i18nKey="dashboard.add_family_member.fields.kinship_degree_proof.description" />
                          )
                        }
                        cardTitle={t(
                          'dashboard.add_family_member.fields.kinship_degree_proof.title'
                        )}
                        files={field.value}
                        onChange={field.onChange}
                        maxFiles={5}
                        error={fieldState.error?.message}
                      />
                    );
                  }}
                />
              ) : null}
            </Stack>
          </Form>
        </FormProvider>
        <BodyText size="sm" color="neutral20">
          {t('dashboard.add_family_member.disclaimer')}
        </BodyText>
      </Stack>
    </Modal>
  );
}
